import React from "react";
import { useParams} from "react-router-dom";
import useGet from "../../../components/hooks/useGet";
import {concepts, Reagent} from "fearless-common";
import {BackendVersion} from "fearless-common/src/backendModels";
import useTitle from "../../../components/hooks/useTitle";
import {DataRow, Pages} from "../../../components/layout";
import {adminRoutes} from "../../../utils/routes";
import moment from "moment";
import {defaultDateTimeFormat} from "../../../utils/date";
import Loading from "../../../components/Loading";
import UrlTabs from "../../../components/UrlTabs";
import ReagentVersionsTable from "./ReagentVersionsTable";
import AdminGroupNameSpan from "../../../components/admin/label-spans/AdminGroupNameSpan";
import AdminUserNameSpan from "../../../components/admin/label-spans/AdminUserNameSpan";

type ReagentWithData = Reagent & {
  versions: Array<BackendVersion<Reagent> & { model: any }>;
  deleted_at: string | null;
};

export default function AdminReagent() {
  const { reagentId } = useParams() as { reagentId: string };
  const { data: reagent, isLoading } = useGet<ReagentWithData>({
    endpoint: `/admin/reagents/${reagentId}?_withTrashed=1&_with[]=versions.user&_with[]=user&_with[]=group&_with[]=labels`
  });

  useTitle(`Admin ${concepts.Group}`);

  return (
    <Pages.Centred>
      <Pages.BackButton href={adminRoutes.reagents}>Reagents</Pages.BackButton>
      <h1>Reagent: {reagent?.name}</h1>
      <div className="d-flex gap-4 mb-3">
        {reagent?.deleted_at && <span>Archived at: {moment(reagent?.deleted_at).format(defaultDateTimeFormat)}</span>}
        <span>Created at: {moment(reagent?.created_at).format(defaultDateTimeFormat)}</span>
        <span>Last updated: {moment(reagent?.updated_at).format(defaultDateTimeFormat)}</span>
      </div>
      {isLoading && <Loading />}
      <UrlTabs
        baseUrl={(k) => adminRoutes.reagent(reagentId, k)}
        queryParameter="tab"
        defaultActiveKey="details"
      >
        <UrlTabs.Tab title="Details" eventKey="details">
          <UrlTabs.TabContent>
            {reagent && (
              <>
                <DataRow label="Name">{reagent.name}</DataRow>
                <DataRow label="Description">{reagent.description}</DataRow>
                <DataRow label="Scope">
                  {reagent.user && <AdminUserNameSpan as="div" user={reagent.user} />}
                  {reagent.group && <AdminGroupNameSpan group={reagent.group} />}
                </DataRow>
                <DataRow label="Labels">
                  {Array.isArray(reagent?.labels) ? (
                    <ul>
                      {reagent.labels.map((label) => (
                        <li>
                          <small>{label.name} - ID {label.id}</small>
                          <small className="text-muted d-block">
                            group_id={label.group_id}, user_id={label.user_id}
                          </small>
                        </li>
                      ))}
                    </ul>
                  ) : <i className="text-muted">None</i>}
                </DataRow>
              </>
            )}
          </UrlTabs.TabContent>
        </UrlTabs.Tab>
        <UrlTabs.Tab title="Versions" eventKey="versions">
          <UrlTabs.TabContent>
            {reagent?.versions && Array.isArray(reagent?.versions) && (
              <ReagentVersionsTable versions={reagent.versions} />
            )}
          </UrlTabs.TabContent>
        </UrlTabs.Tab>
      </UrlTabs>

    </Pages.Centred>
  );
}
